import React, { useState, useEffect, useContext } from 'react';
import {
    Container, Box, TextField, Button, Dialog, DialogContent,
    DialogTitle, Grid, MenuItem, IconButton, InputAdornment,
    Tooltip, Typography, Paper, Checkbox, Autocomplete
} from '@mui/material';
import Swal from 'sweetalert2';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import axios from 'axios';
import ReusableTable from '../components/tables/resuabletable';
import * as XLSX from 'xlsx';
import { AuthContext } from '..';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parse, isValid } from 'date-fns';

export default function Orientations() {
    const [columns, setColumns] = useState([]);
    const [columnOrder, setColumnOrder] = useState([]);
    const [rows, setRows] = useState([]);
    const [openOrientationDialog, setOpenOrientationDialog] = useState(false);
    const [opencolspaper, setOpencolspaper] = useState(false);
    const [editingOrientationId, setEditingOrientationId] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [employeelist, setEmployeelist] = useState([]);
    const [leadslist, setLeadslist] = useState([]);
    const { companyid, selfOnly, employeeId, token, trainersdata } = useContext(AuthContext);
    const [editicon, setEditicon] = useState(true);
    const [deleteicon, setDeleteicon] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState([]);
    const [pagination, setPagination] = useState({
        page: 0,
        rowsPerPage: 3,
    });
    const [totalrows, setTotalrows] = useState(0);
    const [errors, setErrors] = useState({});

    const validateForm = () => {
        const newErrors = {};

        if (!formData.relatedTo) newErrors.relatedTo = 'Lead is required';
        if (!formData.title.trim()) newErrors.title = 'Title is required';
        if (!formData.assignedTo) newErrors.assignedTo = 'Assignee is required';
        if (!formData.startDate) newErrors.startDate = 'Start date is required';
        if (!formData.endDate) newErrors.endDate = 'End date is required';
        if (!formData.description.trim()) newErrors.description = 'Description is required';

        if (editingOrientationId && !formData.status) {
            newErrors.status = 'Status is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handlePaginationChange = (paginationData) => {
        setPagination(paginationData);
    };

    const [formData, setFormData] = useState({
        companyid: '',
        title: '',
        assignedTo: '',
        startDate: null,
        endDate: null,
        relatedTo: '',
        description: '',
        status: '',
        lead_name: ''
    });

    const parseDateSafely = (dateStr) => {
        if (!dateStr) return null;
        if (dateStr instanceof Date && isValid(dateStr)) return dateStr;

        try {
            // Try ISO / database format first
            if (typeof dateStr === 'string' && dateStr.includes('T')) {
                const d = new Date(dateStr);
                if (isValid(d)) return d;
            }

            // Try common patterns
            const patterns = [
                'yyyy-MM-dd',
                'dd/MM/yyyy',
                'MM/dd/yyyy',
                'yyyy-MM-dd HH:mm:ss',
                'dd-MM-yyyy'
            ];

            for (const pattern of patterns) {
                const parsed = parse(String(dateStr), pattern, new Date());
                if (isValid(parsed)) return parsed;
            }

            // Last attempt - native parse
            const native = new Date(dateStr);
            if (isValid(native)) return native;

            return null;
        } catch (err) {
            console.warn('parseDateSafely failed for:', dateStr, err);
            return null;
        }
    };

    const formatDate = (date) => {
        if (!date || !(date instanceof Date) || !isValid(date)) return '';
        try {
            return format(date, 'dd/MM/yyyy');
        } catch (err) {
            console.warn('formatDate failed:', date, err);
            return '';
        }
    };

    const filteredColumns = columnOrder
        .map(field => columns.find(col => col.field === field))
        .filter(col => col && !col.hide);

    useEffect(() => {
        if (companyid) {
            setFormData((prev) => ({ ...prev, companyid: companyid }));
            refreshOrientations();
            fetchLeads();
            setEmployeelist(trainersdata);
        }
    }, [companyid, appliedFilters, pagination]);

    useEffect(() => {
        if (columns.length > 0) {
            setColumnOrder(columns.filter(col => !col.hide).map(col => col.field));
        }
    }, [columns]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const refreshOrientations = () => {
        const payload = {
            filters: JSON.stringify(appliedFilters),
            currentpage: pagination.page,
            rowsPerPage: pagination.rowsPerPage,
            module: true,
            selfOnly: selfOnly,
            employeeid: employeeId
        };

        axios.get(`${process.env.REACT_APP_API_LINKS}/orientations/getorientations`,
            {
                headers: {
                    Authorization: `bearer ${token}`
                },
                params: payload,
                withCredentials: true
            }).then((res) => {
                const newColumns = res.data.columns.map((col) => ({
                    ...col,
                    hide: col.hide || false
                }));
                setColumns(newColumns);
                setColumnOrder(newColumns.filter(col => !col.hide).map(col => col.field));

                const formattedRows = res.data.data.map((orientation) => {
                    return {
                        ...orientation,
                        startDate: parseDateSafely(orientation.start_date),
                        endDate: parseDateSafely(orientation.end_date),
                        createdOn: parseDateSafely(orientation.created_on),
                        services: typeof orientation.services === 'string'
                            ? JSON.parse(orientation.services || '[]')
                            : Array.isArray(orientation.services)
                                ? orientation.services
                                : [],
                    };
                });

                setRows(formattedRows);
                const numberoforientations = res.data.totalrows[0];
                setTotalrows(numberoforientations.totalusers);
            }).catch((error) => {
                console.error('Error fetching orientations:', error);
                setColumns([]);
                setRows([]);
            });
    };

    const fetchLeads = () => {
        axios.get(`${process.env.REACT_APP_API_LINKS}/leads/getdemoleads`, {
            headers: {
                Authorization: `bearer ${token}`
            },
            withCredentials: true
        }).then((res) => {
            setLeadslist(res?.data?.leads || []);
        }).catch((error) => {
            console.error('Error fetching leads:', error);
        });
    };

    const handleOrientationSubmit = (action = null) => {
        // if (!validateForm()) return;

        const lead = leadslist.find(l => l.id === formData.relatedTo);
        const relatedToValue = lead ? lead.id : '';

        const formattedData = {
            ...formData,
            createdby: employeeId,
            relatedTo: relatedToValue,
            startDate: formData.startDate && isValid(formData.startDate)
                ? format(formData.startDate, 'yyyy-MM-dd')
                : null,
            endDate: formData.endDate && isValid(formData.endDate)
                ? format(formData.endDate, 'yyyy-MM-dd')
                : null,
        };

        if (!editingOrientationId) {
            axios.post(
                `${process.env.REACT_APP_API_LINKS}/orientations/addorientation`,
                formattedData,
                {
                    headers: {
                        Authorization: `bearer ${token}`
                    },
                    // params: payload,
                    withCredentials: true
                }
            ).then((res) => {
                if (res.status === 201 || res.status === 200) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Success',
                        text: 'Demo added successfully',
                        showConfirmButton: false,
                        timer: 1500,
                        timerProgressBar: true,
                    });
                    setOpenOrientationDialog(false);
                    refreshOrientations();
                }
            }).catch((err) => {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to add Demo. Try again later.',
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true,
                });
            });
            return;
        }
        axios.put(
            `${process.env.REACT_APP_API_LINKS}/orientations/updateorientation/${editingOrientationId}`,
            formattedData,
            {
                params: action ? { action } : {},
                headers: { Authorization: `bearer ${token}` },
                withCredentials: true
            }
        ).then(() => {
            Swal.fire({
                icon: 'success',
                title: 'Success',
                text:
                    action === 'convert' ? 'Converted to client successfully'
                        : action === 'not_agreed' ? 'Marked as not agreed'
                            : 'Demo updated successfully',
                timer: 1500,
                showConfirmButton: false,
            });
            setOpenOrientationDialog(false);
            setEditingOrientationId(null);
            refreshOrientations();
        }).catch(() => {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Action failed. Try again.',
                timer: 2000,
                showConfirmButton: false,
            });
        });
    };

    // const handleEdit = (orientation) => {
    //     const getEmployeeIdFromName = (name) => {
    //         const match = employeelist.find(emp => emp.employee_name?.toLowerCase() === name?.toLowerCase());
    //         return match ? match.id : '';
    //     };

    //     let relatedValue = '';
    //     const lead = leadslist.find(l => l.lead_name?.toLowerCase() === (orientation.related_to || '')?.toLowerCase());
    //     if (lead) relatedValue = lead.id;

    //     setFormData({
    //         companyid,
    //         title: orientation.title || '',
    //         assignedTo: orientation.employee_id || getEmployeeIdFromName(orientation.assigned_to),
    //         startDate: parseDateSafely(orientation.start_date),
    //         endDate: parseDateSafely(orientation.end_date),
    //         relatedTo: relatedValue,
    //         description: orientation.description || '',
    //         status: orientation.status || '',
    //     });

    //     setEditingOrientationId(orientation.id);
    //     setOpenOrientationDialog(true);
    // };


    const handleEdit = (orientation) => {
        const getEmployeeIdFromName = (name) => {
            const match = employeelist.find(
                emp => emp.employee_name?.toLowerCase() === name?.toLowerCase()
            );
            return match ? match.id : '';
        };

        setFormData({
            companyid,
            title: orientation.title || '',
            assignedTo:
                orientation.employee_id ||
                getEmployeeIdFromName(orientation.assigned_to),
            startDate: parseDateSafely(orientation.start_date),
            endDate: parseDateSafely(orientation.end_date),

            relatedTo: orientation.lead_id || null,

            description: orientation.description || '',
            status: orientation.status || '',
        });

        setEditingOrientationId(orientation.id);
        setOpenOrientationDialog(true);
    };


    const handleDelete = (orientation) => {
        Swal.fire({
            title: `Delete orientation "${orientation.title}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
        }).then((result) => {
            if (result.isConfirmed) {
                axios.delete(`${process.env.REACT_APP_API_LINKS}/orientations/deleteorientation/${orientation.id}`,
                    {
                        headers: {
                            Authorization: `bearer ${token}`
                        },
                        // params: payload,
                        withCredentials: true
                    }
                )
                    .then(() => {
                        Swal.fire('Deleted!', 'Orientation has been deleted.', 'success');
                        refreshOrientations();
                    })
                    .catch(() => {
                        Swal.fire('Error!', 'Failed to delete orientation.', 'error');
                    });
            }
        });
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value.toLowerCase());
    };

    const filteredRows = rows
        .filter((row) => {
            const query = searchQuery.toLowerCase();
            return (
                (row.title || '').toLowerCase().includes(query) ||
                (row.assigned_to || '').toLowerCase().includes(query) ||
                (row.endDate ? formatDate(row.endDate) : '').toLowerCase().includes(query) ||
                (row.status || '').toLowerCase().includes(query)

            );
        })
        .map((row) => {
            const filteredRow = { id: row.id, lead_id: row.lead_id, lead_name: row.lead_name };

            filteredColumns.forEach((col) => {
                if (col.field === 'start_date') {
                    filteredRow[col.field] = formatDate(row.startDate);
                } else if (col.field === 'end_date') {
                    filteredRow[col.field] = formatDate(row.endDate);
                } else if (col.field === 'created_on') {
                    filteredRow[col.field] = formatDate(row.createdOn);
                } else if (col.field === 'lead_name') {
                    filteredRow[col.field] = row.lead_name || '';
                } else {
                    filteredRow[col.field] = row[col.field] || '';
                }
            });

            return filteredRow;
        });


    const formatOrientationsData = (rows) => {
        return rows.map((row, index) => {
            const formattedRow = { S_No: index + 1 };
            filteredColumns.forEach((col) => {
                if (col.headerName === 'S.No') return;
                if (col.field === 'start_date') {
                    formattedRow['Start Date'] = formatDate(row.startDate);
                } else if (col.field === 'end_date') {
                    formattedRow['End Date'] = formatDate(row.endDate);
                } else if (col.field === 'created_on') {
                    formattedRow['Created On'] = formatDate(row.createdOn);
                } else {
                    formattedRow[col.headerName || col.field] = row[col.field] || '';
                }
            });
            return formattedRow;
        });
    };

    const exportToExcel = () => {
        const rowsToExport = selectedIds.length > 0
            ? filteredRows.filter(row => selectedIds.includes(row.id))
            : filteredRows;

        const exportData = formatOrientationsData(rowsToExport);
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Orientations');
        XLSX.writeFile(workbook, 'orientations_data.xlsx');
    };

    const usedLeadIds = rows
        .map(row => row.lead_id)
        .filter(id => id);
    const availableLeads = leadslist.filter((lead) => {
        if (editingOrientationId && lead.id === formData.relatedTo) {
            return true;
        }
        return !usedLeadIds.includes(lead.id);
    });


    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Container maxWidth={false} disableGutters sx={{
                px: { xs: 2, sm: 3, md: 4 },
                py: 3,
                fontFamily: 'Open Sans',
                backgroundColor: '#ffffff',
                minHeight: '100vh',
                boxShadow: { xs: 'none', sm: '0px 0px 5px rgba(187, 222, 241, 0.9)', borderRadius: 10 }
            }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row', md: 'row' },
                        justifyContent: { md: 'space-between' },
                        alignItems: { md: 'center' },
                        gap: 2,
                        mb: 2,
                    }}
                >
                    <TextField
                        placeholder="Search by Title or Assignee"
                        size="small"
                        variant="outlined"
                        sx={{
                            minWidth: 200,
                            width: { sm: 400, md: 500 },
                        }}
                        value={searchQuery}
                        onChange={handleSearch}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'row', sm: 'row', md: 'row' },
                            justifyContent: { xs: 'flex-start', sm: 'space-between', md: 'flex-end' },
                            alignItems: 'center',
                            width: '100%',
                            gap: { xs: 1, sm: 2 },
                        }}
                    >
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => {
                                setFormData({
                                    companyid,
                                    title: '',
                                    assignedTo: '',
                                    startDate: null,
                                    endDate: null,
                                    relatedTo: '',
                                    description: '',
                                    status: ''
                                });
                                setEditingOrientationId(null);
                                setOpenOrientationDialog(true);
                            }}
                            sx={{
                                backgroundColor: '#0b85c8',
                                fontFamily: 'Open Sans',
                                borderRadius: 2,
                                whiteSpace: 'nowrap',
                                minWidth: 'fit-content',
                                width: { xs: '100%', sm: 'auto' }
                            }}
                        >
                            Add Demo
                        </Button>
                        <Tooltip arrow title='Export to Excel'>
                            <IconButton sx={{ color: '#0b85c8' }} onClick={exportToExcel}>
                                <DownloadIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Customize Columns" arrow>
                            <IconButton
                                onClick={() => setOpencolspaper(true)}
                                sx={{ color: '#0b85c8', padding: '8px' }}
                            >
                                <ViewColumnIcon />
                            </IconButton>
                        </Tooltip>

                        {opencolspaper && (
                            <Paper
                                elevation={3}
                                sx={{
                                    p: 2,
                                    zIndex: 1300,
                                    borderRadius: 2,
                                    position: 'absolute',
                                    top: { lg: '160px', xs: '255px', sm: '215px', md: '215px' },
                                    right: '25px',
                                    width: { xs: '280px', sm: '320px', md: '280px' },
                                    maxWidth: '320px',
                                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            fontFamily: 'Open Sans',
                                            fontWeight: 600,
                                            color: '#0b85c8',
                                            fontSize: { xs: '13px', sm: '14px', md: '15px' },
                                        }}
                                    >
                                        Customize Columns
                                    </Typography>
                                    <IconButton onClick={() => setOpencolspaper(false)} size="small">
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                                <Box sx={{ mb: 1 }}>
                                    <Button
                                        variant="text"
                                        onClick={() => {
                                            const allSelected = columns.every(column => !column.hide);
                                            if (allSelected) {
                                                const updatedColumns = columns.map(column => ({ ...column, hide: true }));
                                                setColumns(updatedColumns);
                                                setColumnOrder([]);
                                            } else {
                                                const updatedColumns = columns.map(column => ({ ...column, hide: false }));
                                                setColumns(updatedColumns);
                                                setColumnOrder(columns.map(column => column.field));
                                            }
                                        }}
                                        sx={{
                                            fontFamily: 'Open Sans',
                                            fontSize: { xs: '11px', sm: '12px', md: '13px' },
                                            textTransform: 'none',
                                            color: '#0b85c8',
                                            p: 0,
                                            minWidth: 'auto',
                                            '&:hover': {
                                                backgroundColor: 'transparent',
                                                textDecoration: 'underline'
                                            }
                                        }}
                                    >
                                        {columns.every(column => !column.hide) ? 'Unselect All' : 'Select All'}
                                    </Button>
                                </Box>
                                <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                                    {columns.map((column) => (
                                        <Box key={column.field} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                            <Checkbox
                                                checked={!column.hide}
                                                onChange={(e) => {
                                                    const updatedColumns = columns.map((col) =>
                                                        col.field === column.field ? { ...col, hide: !e.target.checked } : col
                                                    );
                                                    setColumns(updatedColumns);
                                                    if (e.target.checked) {
                                                        setColumnOrder((prev) => {
                                                            if (!prev.includes(column.field)) {
                                                                return [...prev, column.field];
                                                            }
                                                            return prev;
                                                        });
                                                    } else {
                                                        setColumnOrder((prev) => prev.filter(field => field !== column.field));
                                                    }
                                                }}
                                                sx={{ mr: 1, p: 0.5 }}
                                            />
                                            <Typography
                                                sx={{
                                                    fontFamily: 'Open Sans',
                                                    fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                                }}
                                            >
                                                {column.headerName || column.field}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Paper>
                        )}
                    </Box>
                </Box>

                <Box sx={{ overflowX: 'auto', width: '100%' }}>
                    <ReusableTable
                        columns={filteredColumns}
                        rows={filteredRows}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        editicon={editicon}
                        deleteicon={deleteicon}
                        onApplyFilters={(activeFilters) => {
                            setAppliedFilters(activeFilters);
                        }}
                        onPaginationChange={handlePaginationChange}
                        totalrows={totalrows}
                        onSelectionChange={(ids) => setSelectedIds(ids)}
                    />
                </Box>

                <Dialog open={openOrientationDialog} maxWidth="sm" fullWidth>
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ backgroundColor: '#f0f9ff' }}>
                        <DialogTitle sx={{ fontFamily: 'Open Sans', color: '#0b85c8', margin: 'auto', fontWeight: 600 }}>
                            {editingOrientationId ? 'Edit Demo' : 'Add New Demo'}
                        </DialogTitle>
                        <IconButton onClick={() => setOpenOrientationDialog(false)} sx={{ color: '#fff' }}>
                            <CloseIcon sx={{ bgcolor: '#0b85c8', borderRadius: 2, mr: 2 }} />
                        </IconButton>
                    </Box>
                    <DialogContent sx={{ backgroundColor: '#f0f9ff', mt: -2 }}>
                        <Box p={2} sx={{ backgroundColor: '#fff', borderRadius: 2, boxShadow: '1px 1px 10px #888', display: 'flex', justifyContent: 'space-between' }}>
                            <Grid container spacing={1} mt={2} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Autocomplete
                                    options={availableLeads}
                                    getOptionLabel={(option) => option.lead_name}
                                    value={leadslist.find((l) => l.id === formData.relatedTo) || null}
                                    onChange={(e, value) => setFormData({ ...formData, relatedTo: value ? value.id : '' })}
                                    sx={{ width: { lg: '100%', md: '100%' } }}
                                    disabled={!!editingOrientationId}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            {...params}
                                            label="Select Lead"
                                            size="small"
                                            sx={{ width: { lg: '100%', md: '100%' } }}
                                            error={!!errors.relatedTo}
                                            helperText={errors.relatedTo}
                                        />
                                    )}
                                />

                                <TextField
                                    label="Orientation Title"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    fullWidth
                                    size="small"
                                    sx={{ width: { lg: '48%', md: '48%' } }}
                                    error={!!errors.title}
                                    helperText={errors.title}
                                />

                                <TextField
                                    select
                                    label="Assigned To"
                                    name="assignedTo"
                                    value={formData.assignedTo}
                                    onChange={handleInputChange}
                                    fullWidth
                                    size="small"
                                    sx={{ width: { lg: '48%', md: '48%' } }}
                                    error={!!errors.assignedTo}
                                    helperText={errors.assignedTo}
                                >
                                    {employeelist?.map((item) => (
                                        <MenuItem key={item.id} value={item.id}>
                                            {item.employee_name}
                                        </MenuItem>
                                    ))}
                                </TextField>

                                <DatePicker
                                    label="Start Date"
                                    value={formData.startDate}
                                    onChange={(date) => setFormData({ ...formData, startDate: date })}
                                    format="dd/MM/yyyy"
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            sx: {
                                                width: { lg: '48%', md: '48%', sm: '100%' },
                                                '& .MuiInputBase-root': { height: 36 },
                                            },
                                            error: !!errors.startDate,
                                            helperText: errors.startDate,
                                        },
                                    }}
                                />

                                <DatePicker
                                    label="End Date"
                                    value={formData.endDate}
                                    onChange={(date) => setFormData({ ...formData, endDate: date })}
                                    format="dd/MM/yyyy"
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            sx: {
                                                width: { lg: '48%', md: '48%', sm: '100%' },
                                                '& .MuiInputBase-root': { height: 36 },
                                            },
                                            error: !!errors.endDate,
                                            helperText: errors.endDate,
                                        },
                                    }}
                                />

                                <TextField
                                    label="Description"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    fullWidth
                                    multiline
                                    rows={3}
                                    size="small"
                                />

                                {editingOrientationId && (
                                    <TextField
                                        select
                                        name="status"
                                        label="Orientation Status"
                                        value={formData.status}
                                        onChange={handleInputChange}
                                        fullWidth
                                        size='small'
                                    >
                                        <MenuItem value="Started">Started</MenuItem>
                                        <MenuItem value="Pending">Pending</MenuItem>
                                        <MenuItem value="Completed">Completed</MenuItem>
                                    </TextField>
                                )}

                                {editingOrientationId && formData.status === 'Completed' ? (
                                    <Box sx={{ display: 'flex', gap: '20px', justifyContent: 'center', mt: 2, margin: 'auto' }}>
                                        <Button
                                            variant="contained"
                                            color="success"
                                            onClick={() => handleOrientationSubmit('convert')}
                                        >
                                            Convert to Client
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="error"
                                            onClick={() => handleOrientationSubmit('not_agreed')}
                                        >
                                            Not Agreed
                                        </Button>
                                    </Box>
                                ) : (
                                    <Button
                                        variant="contained"
                                        onClick={() => handleOrientationSubmit()}
                                        sx={{ display: 'block', margin: 'auto', mt: 3, background: '#0b85c8' }}
                                    >
                                        {editingOrientationId ? 'Update Demo' : 'Submit Demo'}
                                    </Button>
                                )}
                            </Grid>
                        </Box>
                    </DialogContent>
                </Dialog>
            </Container>
        </LocalizationProvider>
    );
}